/**
 * career-application-review — the entity surface behind `/applications/:id` (highest AX bar).
 *
 * Binds via the `urn` attribute (listed in `entitySurfaces`, so it MUST carry a valid urn).
 * Deep-linkable COLD: `attach()` fetches `/api/applications/:id` (the durable aggregate + any in-flight
 * GenerationRun), seeds the application store via a synthetic Drafted, subscribes to the application +
 * generation streams (live rail during generation), and `resume()`s from the durable truth. Renders
 * the cover letter as SEMANTIC PARAGRAPHS each with its provenance chips, the six quality gates, the
 * review notes, the Kurzprofil + e-mail, the tailored-CV view, the human-gated edit loop
 * (propose → approve/reject, optimistic), the lifecycle transitions, and a ZIP export. Content is
 * domain data; ALL chrome flows through `t()`.
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type Urn,
  type ViewModel,
} from "@platform/web-kit"
import { classMap, html, nothing, repeat, type TemplateResult } from "@platform/web-kit/render"
import {
  ApplicationStoreToken,
  GenerationRunStoreToken,
  I18nToken,
  PushToken,
  RouterToken,
  SessionToken,
  apiGet,
  apiPost,
} from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  applicationStatusClass,
  careerStream,
  type ApplicationGateView,
  type ApplicationStatus,
  type ApplicationStore,
  type ApplicationView,
  type GenerationRunStore,
  type GenerationRunView,
  type ProvenanceEntryView,
  type ShellSession,
} from "../domain"

interface ApplicationDetail {
  application: Record<string, unknown> | null
  generationRun: Record<string, unknown> | null
}

const GATE_KEY: Record<string, CareerKey> = {
  evidence: "review.gate.evidence",
  roleFit: "review.gate.roleFit",
  motivation: "review.gate.motivation",
  clarity: "review.gate.clarity",
  antiSlop: "review.gate.antiSlop",
  readerTest: "review.gate.readerTest",
}
/**
 * German labels for the JSON-Resume section ids the tailoring plan speaks in
 * (`work`, `basics`, `skills`, …) — the review surface must never leak the raw
 * English structural key to the reader. Mirrors `@career/cv-render-v2`'s
 * `DEFAULT_TITLE`; unknown keys fall back to the raw id (still readable).
 */
const SECTION_LABEL: Record<string, string> = {
  basics: "Angaben zur Person",
  summary: "Kurzprofil",
  work: "Berufserfahrung",
  education: "Ausbildung",
  projects: "Projekte",
  skills: "Fähigkeiten",
  languages: "Sprachen",
  certificates: "Zertifikate",
  awards: "Auszeichnungen",
  publications: "Publikationen",
  volunteer: "Ehrenamt",
  interests: "Interessen",
  references: "Referenzen",
}
const sectionLabel = (section: string): string => SECTION_LABEL[section] ?? section

const STATUS_KEY: Record<string, CareerKey> = {
  draft: "applications.status.draft",
  in_review: "applications.status.in_review",
  changes_requested: "applications.status.changes_requested",
  approved: "applications.status.approved",
  sent: "applications.status.sent",
}
/** The legal transitions offered per status (mirrors the api state machine). */
const TRANSITIONS: Record<ApplicationStatus, { to: ApplicationStatus; label: CareerKey }[]> = {
  draft: [{ to: "in_review", label: "review.lifecycle.toReview" }],
  in_review: [
    { to: "approved", label: "review.lifecycle.approve" },
    { to: "changes_requested", label: "review.lifecycle.requestChanges" },
  ],
  changes_requested: [{ to: "in_review", label: "review.lifecycle.toReview" }],
  approved: [
    { to: "sent", label: "review.lifecycle.markSent" },
    { to: "in_review", label: "review.lifecycle.reopen" },
  ],
  sent: [],
}

export class ApplicationReviewVm implements ViewModel {
  readonly app: ReadonlySignal<ApplicationView | undefined>
  readonly genRun: ReadonlySignal<GenerationRunView | undefined>
  readonly editing: Signal<{ index: number; text: string } | null> = signal<{ index: number; text: string } | null>(null)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  readonly urn: Urn
  private readonly shortId: string
  private seq = 1
  private readonly streams = new Set<string>()
  /** Tear-down for the genStore→genRun reactive mirror (set once the run id is known). */
  private stopGenMirror: (() => void) | null = null

  constructor(
    private readonly store: ApplicationStore,
    private readonly genStore: GenerationRunStore,
    private readonly push: PushChannel,
    private readonly router: Router,
    private readonly session: ShellSession,
    i18n: CareerI18n,
    urn: Urn,
  ) {
    this.t = i18n.t
    this.urn = urn
    this.shortId = urn.replace(/^urn:career:application:/, "")
    this.app = store.entity(urn)
    this.genRun = signal<GenerationRunView | undefined>(undefined)
    this.renderDeps = [this.app, this.genRun, this.editing, this.error, this.loaded, store.pending]
  }

  private get userId(): string | null {
    return this.session.user?.sub ?? null
  }

  attach(): () => void {
    void this.hydrate()
    return () => this.stopGenMirror?.()
  }

  /** Paragraphs of the letter body (blank-line split — matches the server segmenter). */
  paragraphs(a: ApplicationView): string[] {
    return a.letterBody.split(/\n{2,}/).map((p) => p.trim()).filter((p) => p.length > 0)
  }

  /** The provenance entries whose anchor falls inside this paragraph → its claim chips. */
  paragraphProvenance(a: ApplicationView, paragraph: string): ProvenanceEntryView[] {
    return a.letterProvenance.filter((e) => e.anchor && paragraph.includes(e.anchor))
  }

  startEdit(index: number, text: string): void {
    this.editing.set({ index, text })
  }
  cancelEdit(): void {
    this.editing.set(null)
  }
  setEditText(text: string): void {
    const cur = this.editing.value
    if (cur) this.editing.set({ ...cur, text })
  }

  async saveEdit(): Promise<void> {
    const cur = this.editing.value
    if (!cur || cur.text.trim().length === 0) return
    this.error.set(null)
    try {
      await apiPost("/api/applications/edit/propose", { applicationId: this.urn, target: { kind: "cover_letter_paragraph", paragraphIndex: cur.index }, after: cur.text.trim() })
      this.editing.set(null)
      await this.hydrate()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    }
  }

  approveEdit(editId: string): void {
    void this.store.command({ type: "edit-approve", payload: { applicationId: this.urn, editId } }).then(() => this.hydrate())
  }
  rejectEdit(editId: string): void {
    void this.store.command({ type: "edit-reject", payload: { applicationId: this.urn, editId } }).then(() => this.hydrate())
  }
  transition(to: ApplicationStatus): void {
    void this.store.command({ type: "transition", payload: { applicationId: this.urn, to } }).then(() => this.hydrate())
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  private async hydrate(): Promise<void> {
    const userId = this.userId
    try {
      const { application, generationRun } = await apiGet<ApplicationDetail>(`/api/applications/${this.shortId}`)
      if (userId && application && typeof application["id"] === "string") {
        this.store.apply({ streamId: careerStream(userId, "Application", application["id"]), version: this.seq++, type: "application.Drafted", payload: { owner: userId, application } })
        this.streams.add(careerStream(userId, "Application", application["id"]))
      }
      if (userId && generationRun && typeof generationRun["id"] === "string") {
        const genId = generationRun["id"]
        const genStream = careerStream(userId, "GenerationRun", genId)
        // Seed the gen rail from the folded run + subscribe live (resume() heals SSE drops).
        this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Started", payload: { owner: userId, generationId: genId, applicationRef: generationRun["applicationRef"] ?? "", startedAt: generationRun["startedAt"] ?? "" } })
        this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Progressed", payload: { owner: userId, generationId: genId, stage: generationRun["stage"] ?? "preselecting", iterations: generationRun["iterations"] ?? 0, costUsd: generationRun["costUsd"] ?? 0, at: generationRun["updatedAt"] ?? "" } })
        if (generationRun["status"] === "completed") {
          this.genStore.apply({ streamId: genStream, version: this.seq++, type: "generationRun.Completed", payload: { owner: userId, generationId: genId, converged: generationRun["converged"] ?? false, costUsd: generationRun["costUsd"] ?? 0, at: generationRun["updatedAt"] ?? "" } })
        }
        // Mirror the store's REACTIVE entity signal into genRun so live folds (the
        // generationRun.Completed that arrives over push when the letter lands) update
        // the rail — a one-time `.value` copy would freeze the run at "running" and the
        // progress banner would never clear once the letter rendered.
        const genEntity = this.genStore.entity(genId as Urn)
        this.stopGenMirror?.()
        this.stopGenMirror = effect([genEntity], ([v]) => (this.genRun as Signal<GenerationRunView | undefined>).set(v))
        this.streams.add(genStream)
        if (generationRun["status"] === "running") this.push.subscribe([...this.streams])
      }
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }
}

export class CareerApplicationReview extends KitElement<ApplicationReviewVm> {
  protected createViewModel(ctx: KitContext): ApplicationReviewVm {
    const { urn } = this
    if (!urn) throw new Error("[career] <career-application-review> needs a urn attribute.")
    return new ApplicationReviewVm(
      ctx.inject(ApplicationStoreToken),
      ctx.inject(GenerationRunStoreToken),
      ctx.inject(PushToken),
      ctx.inject(RouterToken),
      ctx.inject(SessionToken),
      ctx.inject(I18nToken),
      urn,
    )
  }

  private letterPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult {
    const paras = vm.paragraphs(a)
    const editing = vm.editing.value
    return html`
      <section class=${classMap({ "review-letter": true, "surface-panel": true, "is-degraded": a.letterDegraded })} aria-labelledby="review-letter-title">
        <h2 id="review-letter-title" class="surface-section-title" lang="de">${vm.t("review.letter.title")}</h2>
        ${a.letterDegraded ? html`<p class="review-degraded-cue" role="status" lang="de">${vm.t("review.letter.degraded")}</p>` : nothing}
        ${a.letterSubject ? html`<p class="review-letter-subject" lang="de">${a.letterSubject}</p>` : nothing}
        ${a.letterSalutation ? html`<p class="review-letter-salutation" lang="de">${a.letterSalutation}</p>` : nothing}
        <ol class="review-letter-paragraphs" role="list">
          ${paras.map((p, i) => this.paragraph(vm, a, p, i, editing))}
        </ol>
        ${a.letterClosing ? html`<p class="review-letter-closing" lang="de">${a.letterClosing}</p>` : nothing}
        ${a.letterEnclosure ? html`<p class="review-letter-enclosure" lang="de">${a.letterEnclosure}</p>` : nothing}
      </section>
    `
  }

  private paragraph(vm: ApplicationReviewVm, a: ApplicationView, p: string, i: number, editing: { index: number; text: string } | null): TemplateResult {
    const prov = vm.paragraphProvenance(a, p)
    const refs = [...new Set(prov.flatMap((e) => e.refs))]
    if (editing && editing.index === i) {
      return html`
        <li class="review-paragraph is-editing">
          <textarea class="review-edit-text" aria-label=${vm.t("review.edit.propose")} @input=${(e: Event) => vm.setEditText((e.target as HTMLTextAreaElement).value)} lang="de">${editing.text}</textarea>
          <div class="review-edit-actions" role="group">
            <button data-act="edit-save" class="review-edit-save" lang="de">${vm.t("review.edit.save")}</button>
            <button data-act="edit-cancel" class="review-edit-cancel" lang="de">${vm.t("review.edit.cancel")}</button>
          </div>
        </li>
      `
    }
    return html`
      <li class="review-paragraph">
        <p class="review-paragraph-text" lang="de">${p}</p>
        <div class="review-paragraph-meta">
          ${refs.length > 0
            ? html`<ul class="review-paragraph-provenance" role="list" aria-label=${vm.t("review.narrative.provenance", { n: refs.length })}>
                ${refs.map((r) => html`<li class="review-claim-chip chip" urn=${r} lang="de">${vm.t("review.narrative.provenance", { n: 1 })}</li>`)}
              </ul>`
            : nothing}
          <button data-act="propose-edit" data-paragraph=${String(i)} class="review-edit-open" lang="de">${vm.t("review.edit.propose")}</button>
        </div>
      </li>
    `
  }

  private gate(vm: ApplicationReviewVm, g: ApplicationGateView): TemplateResult {
    return html`
      <li class=${classMap({ "review-gate": true, "is-completed": g.pass, "is-failed": !g.pass })}>
        <span class="review-gate-name" lang="de">${vm.t(GATE_KEY[g.gate] ?? "review.gate.clarity")}</span>
        <span class="review-gate-verdict chip" lang="de">${vm.t(g.pass ? "review.gate.pass" : "review.gate.fail")}</span>
      </li>
    `
  }

  private gatesPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (a.gates.length === 0) return nothing
    return html`
      <section class="review-gates surface-panel" aria-labelledby="review-gates-title">
        <h2 id="review-gates-title" class="surface-section-title" lang="de">${vm.t("review.gates.title")}</h2>
        <ul class="review-gate-list" role="list">${a.gates.map((g) => this.gate(vm, g))}</ul>
        ${a.annotations.length > 0
          ? html`<details class="review-annotations">
              <summary lang="de">${vm.t("review.annotations.title")}</summary>
              <ul role="list">
                ${a.annotations.map((an) => html`<li class="review-annotation" lang="de"><span class="review-annotation-severity chip">${an.severity}</span> ${an.comment}${an.span ? html` <span class="review-annotation-span">„${an.span}“</span>` : nothing}</li>`)}
              </ul>
            </details>`
          : nothing}
      </section>
    `
  }

  private kurzprofilPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (!a.kurzprofilBody) return nothing
    return html`
      <section class=${classMap({ "review-kurzprofil": true, "surface-panel": true, "is-degraded": a.kurzprofilDegraded })} aria-labelledby="review-kurzprofil-title">
        <h2 id="review-kurzprofil-title" class="surface-section-title" lang="de">${vm.t("review.kurzprofil.title")}</h2>
        ${a.kurzprofilDegraded ? html`<p class="review-degraded-cue" role="status" lang="de">${vm.t("review.letter.degraded")}</p>` : nothing}
        <p class="review-kurzprofil-text" lang="de">${a.kurzprofilBody}</p>
      </section>
    `
  }

  private emailPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (!a.emailNote) return nothing
    return html`
      <section class="review-email surface-panel" aria-labelledby="review-email-title">
        <h2 id="review-email-title" class="surface-section-title" lang="de">${vm.t("review.email.title")}</h2>
        <p class="review-email-text" lang="de">${a.emailNote}</p>
      </section>
    `
  }

  private tailoringPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    if (a.tailoringSections.length === 0) return nothing
    const sections = [...a.tailoringSections].filter((s) => s.include).sort((x, y) => (x.order ?? 0) - (y.order ?? 0))
    return html`
      <section class="review-tailoring surface-panel" aria-labelledby="review-tailoring-title">
        <h2 id="review-tailoring-title" class="surface-section-title" lang="de">${vm.t("review.tailoring.title")}</h2>
        <ul class="review-tailoring-list" role="list">
          ${sections.map(
            (s) => html`<li class="review-tailoring-section" lang="de">
              <span class="review-tailoring-name">${sectionLabel(s.section)}</span>
              ${s.emphasis ? html`<span class="review-tailoring-what" lang="de">${s.emphasis}</span>` : nothing}
            </li>`,
          )}
        </ul>
      </section>
    `
  }

  private editsPanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult | typeof nothing {
    const pending = a.proposedEdits.filter((e) => e.status === "proposed")
    if (pending.length === 0) return nothing
    return html`
      <section class="review-edits surface-panel" aria-labelledby="review-edits-title">
        <h2 id="review-edits-title" class="surface-section-title" lang="de">${vm.t("review.edit.pending")}</h2>
        <ul class="review-edit-list" role="list">
          ${repeat(pending, (e) => e.id, (e) => html`
            <li class="review-edit is-pending" data-edit=${e.id}>
              <p class="review-edit-after" lang="de">${e.after}</p>
              <div class="review-edit-actions" role="group">
                <button data-act="edit-approve" data-edit=${e.id} class="review-edit-approve" lang="de">${vm.t("review.edit.approve")}</button>
                <button data-act="edit-reject" data-edit=${e.id} class="review-edit-reject" lang="de">${vm.t("review.edit.reject")}</button>
              </div>
            </li>`)}
        </ul>
      </section>
    `
  }

  private lifecyclePanel(vm: ApplicationReviewVm, a: ApplicationView): TemplateResult {
    const next = TRANSITIONS[a.status] ?? []
    return html`
      <section class="review-lifecycle surface-panel" aria-labelledby="review-lifecycle-title">
        <h2 id="review-lifecycle-title" class="surface-section-title" lang="de">${vm.t("review.lifecycle.title")}</h2>
        <p class=${classMap({ "review-lifecycle-current": true, chip: true, [applicationStatusClass(a.status)]: true })} lang="de">${vm.t(STATUS_KEY[a.status] ?? "applications.status.draft")}</p>
        <div class="review-lifecycle-actions" role="group">
          ${next.map((n) => html`<button data-act="transition" data-application=${vm.urn} data-to=${n.to} class="review-transition" lang="de">${vm.t(n.label)}</button>`)}
          <a class="review-download chip" href=${`/api/applications/${vm.urn.replace(/^urn:career:application:/, "")}/package.zip`} data-act="download-zip" data-application=${vm.urn} lang="de">${vm.t("review.download")}</a>
        </div>
      </section>
    `
  }

  protected render(vm: ApplicationReviewVm): TemplateResult {
    const a = vm.app.value
    const run = vm.genRun.value
    const back = html`<a class="review-back chip" href="/applications" data-act="nav" data-url="/applications" lang="de">${vm.t("review.back")}</a>`

    if (!a) {
      const gone = vm.loaded.value
      return html`
        <section class="surface application-review" urn=${vm.urn} aria-labelledby="review-title">
          <h1 id="review-title" class="surface-title" lang="de">${vm.t("surface.bewerbungen.title")}</h1>
          ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}
          ${gone ? html`<p class="surface-empty" lang="de">${vm.t("review.notFound")}</p>` : nothing}
          ${back}
        </section>
      `
    }

    // The progress banner shows only while there is NO letter yet: once
    // `application.CoverLetterAuthored` has folded the (atomic) letter body, the result
    // has landed — the banner must clear even if the gen-run status signal lags. The
    // reactive genRun mirror also clears it on `generationRun.Completed`.
    const generating = !a.letterBody && (run?.status === "running" || !vm.loaded.value)
    return html`
      <section
        class=${classMap({ surface: true, "application-review": true, [applicationStatusClass(a.status)]: true, "is-running": generating, "is-degraded": a.letterDegraded })}
        urn=${vm.urn}
        aria-labelledby="review-title"
      >
        <h1 id="review-title" class="surface-title" lang="de">${a.letterSubject ?? vm.t("surface.bewerbungen.title")}</h1>
        ${vm.error.value ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: vm.error.value })}</p>` : nothing}

        ${generating && run
          ? html`<p class="review-generating" role="status" aria-live="polite" lang="de">
              ${vm.t("review.generating")} <span class="chip">${vm.t(("applications.gen.stage." + run.stage) as CareerKey)}</span>
              ${run.iterations > 0 ? html`<span class="chip">${vm.t("applications.gen.iterations", { n: run.iterations })}</span>` : nothing}
            </p>`
          : nothing}

        ${this.letterPanel(vm, a)}
        ${this.gatesPanel(vm, a)}
        ${this.kurzprofilPanel(vm, a)}
        ${this.emailPanel(vm, a)}
        ${this.tailoringPanel(vm, a)}
        ${this.editsPanel(vm, a)}
        ${this.lifecyclePanel(vm, a)}
        ${back}
      </section>
    `
  }

  protected override actions(vm: ApplicationReviewVm): ActionMap {
    return {
      "propose-edit": ({ paragraph }) => {
        const a = vm.app.value
        if (!a || paragraph === undefined) return
        const idx = Number(paragraph)
        vm.startEdit(idx, vm.paragraphs(a)[idx] ?? "")
      },
      "edit-save": () => void vm.saveEdit(),
      "edit-cancel": () => vm.cancelEdit(),
      "edit-approve": ({ edit }) => {
        if (edit) vm.approveEdit(edit)
      },
      "edit-reject": ({ edit }) => {
        if (edit) vm.rejectEdit(edit)
      },
      transition: ({ to }) => {
        if (to) vm.transition(to as ApplicationStatus)
      },
      "download-zip": () => {
        /* native anchor navigation performs the download; nothing to intercept */
      },
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
